import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../identity/types';
import { employeeReadinessGateway } from './gateway';
import type { EmployeeSetupReadiness } from './types';

/** Every state a readiness read can be in. `forbidden` (403) and `notFound` (404) are distinct from a
 * generic `error` because the UI says something different for each. */
export type ReadinessLoad =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: EmployeeSetupReadiness }
  | { kind: 'error'; message: string }
  | { kind: 'forbidden' }
  | { kind: 'notFound' };

export type ReadinessBatchLoad =
  | { kind: 'loading' }
  | { kind: 'loaded'; byId: Record<string, EmployeeSetupReadiness> }
  | { kind: 'error'; message: string }
  | { kind: 'forbidden' };

type Failure = { kind: 'error'; message: string } | { kind: 'forbidden' } | { kind: 'notFound' };

function toFailure(err: unknown): Failure {
  if (err instanceof ApiError && err.status === 403) return { kind: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { kind: 'notFound' };
  return { kind: 'error', message: err instanceof Error ? err.message : 'Could not load payroll readiness.' };
}

/** One employee's readiness, read from the server. "Loading" is derived (the settled result belongs to a
 * different request key), so no state is set synchronously inside the effect. `reload` re-runs the read —
 * e.g. after the Admin edits something that could change the result. */
export function useEmployeeReadiness(employeeId: string | undefined): { load: ReadinessLoad; reload: () => void } {
  const [nonce, setNonce] = useState(0);
  const key = `${employeeId ?? ''}#${nonce}`;
  const [settled, setSettled] = useState<{ key: string; outcome: ReadinessLoad } | null>(null);

  useEffect(() => {
    if (!employeeId) return;
    const controller = new AbortController();
    employeeReadinessGateway
      .getReadiness(employeeId, undefined, controller.signal)
      .then((data) => setSettled({ key, outcome: { kind: 'loaded', data } }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setSettled({ key, outcome: toFailure(err) });
      });
    return () => controller.abort();
  }, [employeeId, key]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const load: ReadinessLoad = settled?.key === key ? settled.outcome : { kind: 'loading' };
  return { load, reload };
}

/** Readiness for the employees on the visible list page (a single batched request, server limit 100).
 * A failure never blocks the list itself — the caller renders chips in an "unavailable"/"no access" state. */
export function useEmployeeReadinessBatch(employeeIds: readonly string[]): ReadinessBatchLoad {
  const key = employeeIds.join('|');
  const [settled, setSettled] = useState<{ key: string; outcome: ReadinessBatchLoad } | null>(null);

  useEffect(() => {
    if (key === '') return;
    const controller = new AbortController();
    employeeReadinessGateway
      .queryReadiness(key.split('|'), undefined, controller.signal)
      .then((batch) => {
        const byId: Record<string, EmployeeSetupReadiness> = {};
        for (const result of batch.results) byId[result.employeeId] = result;
        setSettled({ key, outcome: { kind: 'loaded', byId } });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const failure = toFailure(err);
        setSettled({
          key,
          outcome: failure.kind === 'forbidden' ? failure : { kind: 'error', message: failure.kind === 'error' ? failure.message : 'Could not load payroll readiness.' },
        });
      });
    return () => controller.abort();
  }, [key]);

  if (key === '') return { kind: 'loaded', byId: {} };
  return settled?.key === key ? settled.outcome : { kind: 'loading' };
}
